import React from "react";
import { useTranslation } from "react-i18next";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
} from "recharts";

export interface TimeActivityDataPoint {
  date: string;
  label: string;
  time: number;
}

interface TimeActivityChartProps {
  title: string;
  data: TimeActivityDataPoint[];
  strokeColor?: string;
  formatValue?: (value: number) => string;
  tooltipContentStyle?: React.CSSProperties;
  tooltipLabelKey?: string;
}

/** Y-axis tick: clean format for time values */
function CustomYAxisTick(props: {
  x?: number;
  y?: number;
  payload?: { value?: number };
  value?: number;
  formatValue?: (v: number) => string;
}) {
  const { x = 0, y = 0, payload, value, formatValue } = props;
  const displayValue = payload?.value ?? value ?? 0;
  const formatted = formatValue ? formatValue(displayValue) : `${Math.round(displayValue)}`;
  const fill = "hsl(var(--muted-foreground) / 0.9)";
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={4} textAnchor="end" fill={fill} fontSize={11} fontWeight={500}>
        {formatted}
      </text>
    </g>
  );
}

/** Tooltip: date + value — clean analytics style */
function TimeActivityTooltip({
  active,
  payload,
  contentStyle,
  formatValue,
  tooltipLabelKey,
}: TooltipProps<number, string> & {
  contentStyle: React.CSSProperties;
  formatValue?: (value: number) => string;
  tooltipLabelKey?: string;
}) {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  const value = p.value as number;
  const label = p.payload?.label ?? p.payload?.date ?? "";
  const formatted = formatValue ? formatValue(value) : `${Math.round(value)} мин`;

  return (
    <div style={contentStyle}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 500,
          color: "hsl(var(--muted-foreground))",
          letterSpacing: "0.03em",
          marginBottom: 8,
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 15,
          fontWeight: 600,
          color: "hsl(var(--foreground))",
          letterSpacing: "-0.01em",
        }}
      >
        {tooltipLabelKey ? `${tooltipLabelKey}: ` : ""}
        {formatted}
      </div>
    </div>
  );
}

/**
 * Daily time activity chart: shows minutes per day (not cumulative).
 * Smooth curve, gradient fill, natural daily variation.
 */
export default function TimeActivityChart({
  title,
  data,
  strokeColor = "hsl(var(--accent3))",
  formatValue,
  tooltipContentStyle,
  tooltipLabelKey,
}: TimeActivityChartProps) {
  const { t } = useTranslation();
  const color = strokeColor;
  const gradientId = `timeActivity-${React.useId().replace(/:/g, "")}`;
  const defaultFormatValue = (v: number) => {
    if (v >= 60) {
      const h = Math.floor(v / 60);
      const m = Math.round(v % 60);
      return m > 0 ? `${h} ч ${m} мин` : `${h} ч`;
    }
    return `${Math.round(v)} мин`;
  };
  const actualFormatValue = formatValue ?? defaultFormatValue;
  const tooltipStyle: React.CSSProperties = tooltipContentStyle ?? {
    backgroundColor: "hsl(var(--card-bg))",
    border: "1px solid hsl(var(--border) / 0.6)",
    borderRadius: 14,
    padding: "14px 18px",
    color: "hsl(var(--foreground))",
    fontSize: 13,
    boxShadow: "0 12px 32px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)",
    minWidth: 140,
  };

  return (
    <div className="w-full max-w-full min-w-0 overflow-hidden">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 14,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "hsl(var(--foreground))",
            letterSpacing: "-0.01em",
          }}
        >
          {title}
        </span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 500,
            letterSpacing: "0.04em",
            padding: "2px 8px",
            borderRadius: 6,
            background: "hsl(var(--primary) / 0.08)",
            color: "hsl(var(--muted-foreground))",
          }}
        >
          {t("statistics.perDay")}
        </span>
      </div>

      {data.length === 0 ? (
        <div className="statistics-empty" style={{ minHeight: 180 }}>
          {t("statistics.noData")}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={220} minHeight={180}>
          <AreaChart data={data} margin={{ top: 20, right: 20, left: 12, bottom: 16 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.38} />
                <stop offset="45%" stopColor={color} stopOpacity={0.14} />
                <stop offset="100%" stopColor={color} stopOpacity={0.01} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="1 4"
              stroke="hsl(var(--border) / 0.5)"
              strokeOpacity={0.2}
              vertical={false}
            />
            <XAxis
              dataKey="label"
              minTickGap={48}
              interval={data.length > 14 ? Math.max(0, Math.floor(data.length / 7)) : 0}
              tick={{
                fill: "hsl(var(--muted-foreground) / 0.9)",
                fontSize: 11,
                fontWeight: 500,
              }}
              axisLine={false}
              tickLine={false}
              padding={{ left: 10, right: 10 }}
            />
            <YAxis
              domain={[0, (dataMax: number) => Math.ceil(Math.max(dataMax, 10) * 1.12)]}
              allowDecimals={false}
              axisLine={false}
              tickLine={false}
              width={52}
              tick={<CustomYAxisTick formatValue={(v) => actualFormatValue(v)} />}
            />
            <Tooltip
              wrapperStyle={{ outline: "none" }}
              cursor={{ stroke: "hsl(var(--border) / 0.4)", strokeWidth: 1, strokeDasharray: "4 4" }}
              content={(props) => (
                <TimeActivityTooltip
                  {...props}
                  contentStyle={tooltipStyle}
                  formatValue={actualFormatValue}
                  tooltipLabelKey={tooltipLabelKey}
                />
              )}
            />
            <Area
              type="monotoneX"
              dataKey="time"
              stroke={color}
              strokeWidth={2.5}
              fill={`url(#${gradientId})`}
              fillOpacity={1}
              baseLine={0}
              dot={false}
              activeDot={{
                r: 6,
                strokeWidth: 2.5,
                stroke: "hsl(var(--card-bg))",
                fill: color,
              }}
              connectNulls
              isAnimationActive
              animationDuration={600}
              animationEasing="ease-out"
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
